import { useEffect, useState, useCallback } from "react";
import { NotebookPen, Plus, Pin, PinOff, Trash2, Save, Loader2, StickyNote, Mic, MicOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotes } from "@/context/NotesContext";
import { useSpeechToText } from "@/hooks/useSpeechToText";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const CTX_LABEL = { general: "General", learn: "Pula Learn", market: "Pula Market", properties: "Pula Properties", jobs: "Pula Jobs", love: "Pula Love", live: "Pula Live" };

export const NotesDrawer = () => {
  const { open, setOpen, scope } = useNotes();
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ title: "", content: "" });
  const [saving, setSaving] = useState(false);

  const appendSpeech = useCallback((text) => {
    setDraft((d) => ({ ...d, content: (d.content ? d.content + " " : "") + text }));
  }, []);
  const speech = useSpeechToText({ onResult: appendSpeech });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (scope.context_ref) params.context_ref = scope.context_ref;
      else if (scope.context && scope.context !== "all") params.context = scope.context;
      const { data } = await api.get("/notes", { params });
      setNotes(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [scope]);

  useEffect(() => {
    if (open && user) { load(); setDraft({ title: "", content: "" }); }
  }, [open, user, load]);

  const addNote = async () => {
    if (!draft.title.trim() && !draft.content.trim()) { toast.error("Write something first."); return; }
    setSaving(true);
    try {
      await api.post("/notes", { ...draft, context: scope.context || "general", context_label: scope.context_label, context_ref: scope.context_ref });
      setDraft({ title: "", content: "" });
      toast.success("Note saved.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save note."); }
    finally { setSaving(false); }
  };

  const saveNote = async (n) => {
    try { await api.put(`/notes/${n.note_id}`, n); toast.success("Note updated."); load(); }
    catch { toast.error("Could not update note."); }
  };
  const togglePin = async (n) => { await api.put(`/notes/${n.note_id}`, { ...n, pinned: !n.pinned }); load(); };
  const remove = async (id) => { await api.delete(`/notes/${id}`); toast.success("Note deleted."); load(); };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0" data-testid="notes-drawer">
        <SheetHeader className="p-5 border-b border-[var(--pula-border)]">
          <SheetTitle className="font-heading flex items-center gap-2"><NotebookPen className="w-5 h-5 text-pula-green" /> My Notes</SheetTitle>
          <SheetDescription>
            {scope.context_label ? `Notes for: ${scope.context_label}` : `${CTX_LABEL[scope.context] || "General"} notes · private to you`}
          </SheetDescription>
        </SheetHeader>

        {!user ? (
          <div className="flex-1 grid place-items-center p-6 text-center">
            <div>
              <StickyNote className="w-8 h-8 text-pula-green mx-auto mb-2" />
              <p className="font-body text-pula-inksoft">Sign in to take and save notes.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Composer */}
            <div className="p-4 border-b border-[var(--pula-border)] bg-pula-bg2">
              <Input data-testid="note-title" placeholder="Note title (optional)" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="mb-2 bg-white" />
              <div className="relative">
                <Textarea data-testid="note-content" rows={3} placeholder="Write your note… or tap the mic to dictate" value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} className="bg-white pr-11" />
                {speech.supported && (
                  <button data-testid="note-mic" onClick={speech.toggle} title={speech.listening ? "Stop dictation" : "Dictate note"}
                    className={`absolute top-2 right-2 w-8 h-8 rounded-full grid place-items-center transition-colors ${speech.listening ? "bg-pula-accent text-white animate-pulse" : "bg-white border border-[var(--pula-border)] text-pula-inksoft hover:text-pula-green"}`}>
                    {speech.listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                )}
              </div>
              {speech.listening && <p className="text-xs text-pula-accent font-body mt-1">Listening… speak now.</p>}
              {!speech.supported && <p className="text-[11px] text-pula-inksoft font-body mt-1">Voice dictation works best in Chrome, Edge or Safari.</p>}
              <Button data-testid="note-add" onClick={addNote} disabled={saving} className="mt-2 w-full rounded-full bg-pula-green hover:bg-pula-greenhover text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add note</>}
              </Button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="notes-list">
              {loading ? <Loader2 className="w-5 h-5 animate-spin text-pula-green mx-auto" /> :
                notes.length === 0 ? <p className="text-sm text-pula-inksoft font-body text-center py-6">No notes yet. Add your first above.</p> :
                notes.map((n) => <NoteCard key={n.note_id} note={n} onSave={saveNote} onPin={togglePin} onDelete={remove} />)}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

function NoteCard({ note, onSave, onPin, onDelete }) {
  const [edit, setEdit] = useState({ title: note.title, content: note.content });
  const dirty = edit.title !== note.title || edit.content !== note.content;
  return (
    <div className="pula-card p-3" data-testid={`note-${note.note_id}`}>
      <div className="flex items-center justify-between gap-2">
        <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
          placeholder="Untitled" className="flex-1 bg-transparent font-heading font-bold text-sm text-pula-ink outline-none" />
        <div className="flex items-center gap-1 shrink-0">
          <button data-testid={`note-pin-${note.note_id}`} onClick={() => onPin(note)} className="p-1 text-pula-inksoft hover:text-pula-olive transition-colors">
            {note.pinned ? <Pin className="w-3.5 h-3.5 fill-pula-olive text-pula-olive" /> : <PinOff className="w-3.5 h-3.5" />}
          </button>
          <button data-testid={`note-delete-${note.note_id}`} onClick={() => onDelete(note.note_id)} className="p-1 text-pula-inksoft hover:text-destructive transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <textarea value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} rows={3}
        className="w-full mt-1 bg-transparent text-sm font-body text-pula-inksoft outline-none resize-none" />
      {note.context_label && <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-pula-green/10 text-pula-olive">{note.context_label}</span>}
      {dirty && (
        <Button data-testid={`note-save-${note.note_id}`} size="sm" onClick={() => onSave({ ...note, ...edit })} className="mt-2 rounded-full bg-pula-green hover:bg-pula-greenhover text-white h-7 text-xs">
          <Save className="w-3 h-3 mr-1" /> Save
        </Button>
      )}
    </div>
  );
}
